import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    Card,
    CardMedia,
    CardContent,
    Modal,
    IconButton,
    Divider
} from '@mui/material';
import { CloseIcon, PlayArrowIcon, ChevronLeftIcon, ChevronRightIcon } from 'icons/materialIcons';

const ProductsServicesPreview = ({
    link,
    cardLinkColor = '#000',
    isFullDisplay = false // For expanded view vs compact card view
}) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    if (!link || !link.details) return null;

    const {
        productTitle,
        description,
        price,
        category,
        features = [],
        images = [],
        videos = [],
        ctaButton
    } = link.details;

    // Navigate gallery
    const nextImage = () => {
        setSelectedImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
        setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Handle CTA button click
    const handleCtaClick = () => {
        if (ctaButton?.link) {
            window.open(ctaButton.link, '_blank', 'noopener,noreferrer');
        }
    };

    // Handle video click
    const handleVideoClick = (videoUrl) => {
        if (videoUrl.startsWith('http')) {
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
        } else {
            setIsVideoPlaying(true);
        }
    };

    // Compact version for card display
    if (!isFullDisplay) {
        return (
            <Card
                sx={{
                    maxWidth: 300,
                    mb: 2,
                    boxShadow: 2,
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                {/* Product Image/Video */}
                {images.length > 0 && (
                    <Box sx={{ position: 'relative', height: 180 }}>
                        <CardMedia
                            component="img"
                            height="180"
                            image={images[selectedImageIndex]}
                            alt={productTitle}
                            sx={{
                                objectFit: 'cover',
                                cursor: 'pointer'
                            }}
                            onClick={() => setIsGalleryOpen(true)}
                        />

                        {/* Image indicators */}
                        {images.length > 1 && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    gap: 0.5
                                }}
                            >
                                {images.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: index === selectedImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => setSelectedImageIndex(index)}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Video indicator */}
                        {videos.length > 0 && (
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                }}
                                size="small"
                                onClick={() => handleVideoClick(videos[0])}
                            >
                                <PlayArrowIcon />
                            </IconButton>
                        )}
                    </Box>
                )}

                <CardContent sx={{ p: 2 }}>
                    {/* Product Title */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {productTitle}
                    </Typography>

                    {/* Category and Price */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        {category && (
                            <Chip
                                label={category}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                            />
                        )}
                        {price && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: cardLinkColor,
                                    fontSize: '0.9rem'
                                }}
                            >
                                {price}
                            </Typography>
                        )}
                    </Box>

                    {/* Description */}
                    {description && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.8rem',
                                mb: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                            }}
                        >
                            {description}
                        </Typography>
                    )}

                    {/* Features (show first 2) */}
                    {features.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {features.slice(0, 2).map((feature, index) => (
                                    <Chip
                                        key={index}
                                        label={feature}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: 20,
                                            backgroundColor: 'rgba(0,0,0,0.05)',
                                            '& .MuiChip-label': { px: 1 }
                                        }}
                                    />
                                ))}
                                {features.length > 2 && (
                                    <Chip
                                        label={`+${features.length - 2} more`}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: 20,
                                            backgroundColor: 'rgba(0,0,0,0.1)',
                                            '& .MuiChip-label': { px: 1 }
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* CTA Button */}
                    {ctaButton?.text && (
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={handleCtaClick}
                            sx={{
                                backgroundColor: cardLinkColor,
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                py: 1,
                                '&:hover': {
                                    backgroundColor: cardLinkColor,
                                    opacity: 0.9
                                }
                            }}
                        >
                            {ctaButton.text}
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Full display version (for modal or expanded view)
    return (
        <>
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                {/* Product Images/Videos Gallery */}
                {images.length > 0 && (
                    <Box sx={{ position: 'relative', mb: 3 }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: 300,
                                borderRadius: 2,
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                        >
                            <img
                                src={images[selectedImageIndex]}
                                alt={productTitle}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setIsGalleryOpen(true)}
                            />

                            {/* Navigation arrows */}
                            {images.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={prevImage}
                                        sx={{
                                            position: 'absolute',
                                            left: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                        }}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={nextImage}
                                        sx={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                        }}
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </>
                            )}
                        </Box>

                        {/* Thumbnail strip */}
                        {images.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto', pb: 1 }}>
                                {images.map((image, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => setSelectedImageIndex(index)}
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            border: index === selectedImageIndex ? `2px solid ${cardLinkColor}` : '2px solid transparent',
                                            opacity: index === selectedImageIndex ? 1 : 0.7
                                        }}
                                    >
                                        <img
                                            src={image}
                                            alt={`${productTitle} ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Product Info */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                        {productTitle}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        {category && (
                            <Chip
                                label={category}
                                variant="outlined"
                                sx={{ fontSize: '0.9rem' }}
                            />
                        )}
                        {price && (
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    color: cardLinkColor
                                }}
                            >
                                {price}
                            </Typography>
                        )}
                    </Box>

                    {description && (
                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                            {description}
                        </Typography>
                    )}

                    {/* Features */}
                    {features.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Key Features
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {features.map((feature, index) => (
                                    <Chip
                                        key={index}
                                        label={feature}
                                        variant="outlined"
                                        sx={{ fontSize: '0.9rem' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Videos */}
                    {videos.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto' }}>
                            {videos.map((video, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        minWidth: 200,
                                        height: 150,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => handleVideoClick(video.src)}
                                >
                                    {video.thumbnail ? (
                                        <img
                                            src={video.thumbnail}
                                            alt={`Video thumbnail ${index + 1}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <video
                                            src={video.src}
                                            poster={video.thumbnail || undefined}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            controls={false}
                                            muted
                                            preload="metadata"
                                        />
                                    )}
                                    <IconButton
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                        }}
                                        size="small"
                                    >
                                        <PlayArrowIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* CTA Button */}
                    {ctaButton?.text && (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleCtaClick}
                            sx={{
                                backgroundColor: cardLinkColor,
                                textTransform: 'none',
                                fontSize: '1rem',
                                py: 1.5,
                                px: 4,
                                '&:hover': {
                                    backgroundColor: cardLinkColor,
                                    opacity: 0.9
                                }
                            }}
                        >
                            {ctaButton.text}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Image Gallery Modal */}
            <Modal
                open={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    outline: 'none'
                }}>
                    <IconButton
                        onClick={() => setIsGalleryOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={images[selectedImageIndex]}
                        alt={productTitle}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: 8
                        }}
                    />
                </Box>
            </Modal>

            {/* Video Modal - Only for local videos */}
            <Modal
                open={isVideoPlaying}
                onClose={() => setIsVideoPlaying(false)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    outline: 'none'
                }}>
                    <IconButton
                        onClick={() => setIsVideoPlaying(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {videos.length > 0 && !videos[0].startsWith('http') && (
                        <video
                            src={videos[0]}
                            controls
                            autoPlay
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: 8
                            }}
                        />
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default ProductsServicesPreview;
